import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Modal } from "../../../ui/components/Modal";
import { Callout } from "../../../ui/components/Callout";
import { TextInput } from "../../../ui/components/Form";
import { Switch } from "../../../ui/components/Switch";
import { useI18n } from "../../lib/i18n";
import { useCertificationContentDefinitions } from "../../lib/i18n/useCertificationContentDefinitions";
import { isContentLocKey } from "../../lib/i18n/contentKeys";
import { certificationModulesQueryOptions } from "../../lib/api/certification-queries";
import { listGlobalRoles, listPermissions } from "../../lib/api/admin-users";
import { createRewardRule, updateRewardRule, deleteRewardRule } from "../../lib/api/certification-reward-rules";
import type { RewardRule } from "../../lib/api/certification-reward-rules";
import * as s from "./RewardRuleFormModal.css";

export interface RewardRuleFormModalProps {
  open: boolean;
  onClose: () => void;
  accessToken: string;
  /** When provided, editing an existing rule. Otherwise, creating a new one. */
  existing?: RewardRule;
  onSaved: (rule: RewardRule) => void;
  onDeleted: (ruleId: string) => void;
}

export function RewardRuleFormModal({
  open,
  onClose,
  accessToken,
  existing,
  onSaved,
  onDeleted,
}: RewardRuleFormModalProps) {
  const { t } = useI18n();
  const { resolve } = useCertificationContentDefinitions();
  const resolveModuleTitle = React.useCallback(
    (title: string) => (isContentLocKey(title) ? resolve(title) || "…" : title),
    [resolve],
  );
  const isEdit = !!existing;

  const [name, setName] = React.useState("");
  const [enabled, setEnabled] = React.useState(true);
  const [requirementModuleIds, setRequirementModuleIds] = React.useState<Set<string>>(new Set());
  const [rewardRoleIds, setRewardRoleIds] = React.useState<Set<string>>(new Set());
  const [rewardPermissionIds, setRewardPermissionIds] = React.useState<Set<string>>(new Set());
  const [moduleFilter, setModuleFilter] = React.useState("");
  const [permissionFilter, setPermissionFilter] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setName(existing?.name ?? "");
    setEnabled(existing?.enabled ?? true);
    setRequirementModuleIds(new Set(existing?.requirements.map((r) => r.moduleId) ?? []));
    setRewardRoleIds(new Set(existing?.rewards.roles.map((r) => r.roleId) ?? []));
    setRewardPermissionIds(new Set(existing?.rewards.permissions.map((p) => p.permissionId) ?? []));
    setModuleFilter("");
    setPermissionFilter("");
    setError(null);
    setSuccessMessage(null);
  }, [open, existing]);

  const modulesQuery = useQuery({
    ...certificationModulesQueryOptions(accessToken, { includeInactive: true }),
    enabled: open,
  });
  const rolesQuery = useQuery({
    queryKey: ["reward-rule-form-global-roles"],
    queryFn: () => listGlobalRoles(accessToken),
    enabled: open,
    staleTime: 60_000,
  });
  const permissionsQuery = useQuery({
    queryKey: ["reward-rule-form-permissions"],
    queryFn: () => listPermissions(accessToken),
    enabled: open,
    staleTime: 60_000,
  });

  const modules = modulesQuery.data ?? [];
  const roles = rolesQuery.data ?? [];
  const permissions = permissionsQuery.data ?? [];

  const filteredModules = React.useMemo(() => {
    const q = moduleFilter.trim().toLowerCase();
    if (!q) return modules;
    return modules.filter(
      (m) => resolveModuleTitle(m.title).toLowerCase().includes(q) || m.slug.toLowerCase().includes(q),
    );
  }, [modules, moduleFilter, resolveModuleTitle]);

  const groupedPermissions = React.useMemo(() => {
    const q = permissionFilter.trim().toLowerCase();
    const filtered = q
      ? permissions.filter((p) => p.name.toLowerCase().includes(q) || (p.description ?? "").toLowerCase().includes(q))
      : permissions;
    const map = new Map<string, typeof filtered>();
    for (const p of filtered) {
      const resource = p.resource || p.name.split(":")[0] || "other";
      const list = map.get(resource) ?? [];
      list.push(p);
      map.set(resource, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [permissions, permissionFilter]);

  const toggle = (set: Set<string>, setSet: (s: Set<string>) => void, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSet(next);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError(t("pro.system.rewardRules.modal.nameRequired"));
      return;
    }
    if (requirementModuleIds.size === 0) {
      setError(t("pro.system.rewardRules.modal.requirementsRequired"));
      return;
    }
    if (rewardRoleIds.size === 0 && rewardPermissionIds.size === 0) {
      setError(t("pro.system.rewardRules.modal.rewardsRequired"));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: name.trim(),
        enabled,
        requirementModuleIds: [...requirementModuleIds],
        rewardRoleIds: [...rewardRoleIds],
        rewardPermissionIds: [...rewardPermissionIds],
      };
      const saved = isEdit
        ? await updateRewardRule(accessToken, existing!.id, payload)
        : await createRewardRule(accessToken, payload);

      onSaved(saved);
      if (saved.retroactive && saved.retroactive.granted > 0) {
        // Keep the modal open so the admin actually sees this -- there's no
        // global toast host mounted in this app to show it after closing.
        setSuccessMessage(
          t("pro.system.rewardRules.modal.retroactiveToastBody").replace(
            "{count}",
            String(saved.retroactive.granted),
          ),
        );
      } else {
        onClose();
      }
    } catch (err: any) {
      setError(err?.message ?? t("pro.system.rewardRules.modal.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existing) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteRewardRule(accessToken, existing.id);
      onDeleted(existing.id);
      onClose();
    } catch (err: any) {
      setError(err?.message ?? t("pro.system.rewardRules.modal.deleteError"));
    } finally {
      setDeleting(false);
    }
  };

  const busy = saving || deleting;

  return (
    <Modal open={open} setOpen={(v) => { if (!v) onClose(); }} minWidth="520px" maxWidth="640px" closeOnClickOff>
      <Modal.Form onSubmit={handleSubmit}>
        <Modal.Header>
          <Text level="h3" weight="bold">
            {isEdit ? t("pro.system.rewardRules.modal.editTitle") : t("pro.system.rewardRules.modal.createTitle")}
          </Text>
        </Modal.Header>
        <Modal.Body>
          <div className={s.body}>
            {error && <Callout variant="error">{error}</Callout>}
            {successMessage && <Callout variant="success">{successMessage}</Callout>}

            <TextInput
              label={t("pro.system.rewardRules.modal.nameLabel")}
              value={name}
              onChange={(e) => { setName(e.target.value); setError(null); }}
              disabled={busy}
              placeholder={t("pro.system.rewardRules.modal.namePlaceholder")}
              autoFocus
            />

            <Switch
              label={t("pro.system.rewardRules.modal.enabledLabel")}
              checked={enabled}
              disabled={busy}
              onCheckedChange={setEnabled}
            />

            <div className={s.section}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                <h4 className={s.sectionTitle}>{t("pro.system.rewardRules.modal.requirementsSection")}</h4>
                <span className={s.selectedCount}>{requirementModuleIds.size} selected</span>
              </div>
              <p className={s.sectionHint}>{t("pro.system.rewardRules.modal.requirementsHint")}</p>
              <TextInput
                value={moduleFilter}
                onChange={(e) => setModuleFilter(e.target.value)}
                placeholder={t("pro.system.rewardRules.modal.filterModulesPlaceholder")}
                disabled={busy}
              />
              <div className={s.pickerList}>
                {filteredModules.length === 0 ? (
                  <span className={s.pickerEmpty}>{t("pro.system.rewardRules.modal.noModules")}</span>
                ) : (
                  filteredModules.map((m) => (
                    <label key={m.id} className={s.pickerRow}>
                      <input
                        type="checkbox"
                        checked={requirementModuleIds.has(m.id)}
                        disabled={busy}
                        onChange={() => toggle(requirementModuleIds, setRequirementModuleIds, m.id)}
                      />
                      <span className={s.pickerRowMain}>
                        <span className={s.pickerRowTitle}>{resolveModuleTitle(m.title)}</span>
                        <span className={s.pickerRowSubtitle}>{m.slug}</span>
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>

            <div className={s.section}>
              <h4 className={s.sectionTitle}>{t("pro.system.rewardRules.modal.rewardsSection")}</h4>

              <span className={s.pickerGroupTitle}>{t("pro.system.rewardRules.modal.rolesSubsection")}</span>
              <div className={s.pickerList}>
                {roles.length === 0 ? (
                  <span className={s.pickerEmpty}>{t("pro.system.rewardRules.modal.noRoles")}</span>
                ) : (
                  roles.map((r) => (
                    <label key={r.id} className={s.pickerRow}>
                      <input
                        type="checkbox"
                        checked={rewardRoleIds.has(r.id)}
                        disabled={busy}
                        onChange={() => toggle(rewardRoleIds, setRewardRoleIds, r.id)}
                      />
                      <span className={s.pickerRowMain}>
                        <span className={s.pickerRowTitle}>{r.name}</span>
                      </span>
                    </label>
                  ))
                )}
              </div>

              <span className={s.pickerGroupTitle}>{t("pro.system.rewardRules.modal.permissionsSubsection")}</span>
              <TextInput
                value={permissionFilter}
                onChange={(e) => setPermissionFilter(e.target.value)}
                placeholder={t("pro.system.rewardRules.modal.filterPermissionsPlaceholder")}
                disabled={busy}
              />
              <div className={s.pickerList}>
                {groupedPermissions.length === 0 ? (
                  <span className={s.pickerEmpty}>{t("pro.system.rewardRules.modal.noPermissions")}</span>
                ) : (
                  groupedPermissions.map(([resource, perms]) => (
                    <div key={resource} className={s.pickerGroup}>
                      <span className={s.pickerGroupTitle}>{resource}</span>
                      {perms.map((p) => (
                        <label key={p.id} className={s.pickerRow}>
                          <input
                            type="checkbox"
                            checked={rewardPermissionIds.has(p.id)}
                            disabled={busy}
                            onChange={() => toggle(rewardPermissionIds, setRewardPermissionIds, p.id)}
                          />
                          <span className={s.pickerRowMain}>
                            <span className={s.pickerRowTitle}>{p.name}</span>
                            {p.description && <span className={s.pickerRowSubtitle}>{p.description}</span>}
                          </span>
                        </label>
                      ))}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          {successMessage ? (
            <Button type="button" color="primary" variant="solid" onClick={onClose}>
              {t("shared.common.close")}
            </Button>
          ) : (
            <>
              {isEdit && (
                <Button
                  type="button"
                  color="danger"
                  variant="outline"
                  onClick={handleDelete}
                  loading={deleting}
                  disabled={saving}
                  style={{ marginRight: "auto" }}
                >
                  {t("pro.system.rewardRules.modal.delete")}
                </Button>
              )}
              <Button type="button" color="primary" variant="outline" onClick={onClose} disabled={busy}>
                {t("shared.common.cancel")}
              </Button>
              <Button type="submit" color="primary" variant="solid" loading={saving} disabled={deleting}>
                {t("shared.common.save")}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal.Form>
    </Modal>
  );
}
